import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Button, InfoMessage, Input, Label, Modal, ModalInner, SelectBox, Text } from 'components';

import { GroundStation, RegisterContactProfile } from './types';
import axios from 'common/AxiosAuth';
import { ListType } from 'types/selectbox';
import { Message } from 'types/message';
import { InformationCircleOutlineIcon } from 'assets/svgs';
import { registerContactProfileList } from './utils';
import { convertAltitudeToKm } from 'utils';
import { fetchGroundStationList } from 'api/groundStation';

interface ModalProps {
  isOpened: boolean;
  setIsOpened: Dispatch<SetStateAction<boolean>>;
  onContactProfileUpdated: () => Promise<void>;
}

interface RegisterConfirmProps {
  onCancel: () => void;
  onDone: () => void;
  registerData: RegisterContactProfile;
  onPrevious: () => void;
}

interface RegisterFormProps {
  onCancel: () => void;
  setRegisterData: Dispatch<SetStateAction<RegisterContactProfile | null>>;
  initialData?: RegisterContactProfile | null; // 폼에 전달할 초기 데이터
}

interface RegisterConfirmCardProps {
  title: string | number;
  label: string;
}

const RegisterConfirmCard = ({ title, label }: RegisterConfirmCardProps) => {
  return (
    <div className="flex h-[120px] flex-col items-center  justify-center gap-1 rounded-lg border border-border-primary">
      <Text size="body1" color="primary" weight="medium" className='text-center'>
        {title.toString() ? title.toString() : '-'}
      </Text>
      <Text size="body2" color="tertiary" weight="regular">
        {label}
      </Text>
    </div>
  );
};

const RegisterConfirm = ({ registerData, onDone , onPrevious }: RegisterConfirmProps) => {
  const registerDataLabels = (Object.keys(registerData) as (keyof RegisterContactProfile)[]).filter(
    (label) => label !== 'groundstationId'
  );
  const registerContactProfileLabels = registerContactProfileList.reduce((acc, value) => ({
    ...acc,
    [value.key]: value.title,
}), {} as {[key in 'groundstationId' | 'groundstationName' | 'name' | 'minimumContactDuration' | 'minimumDegrees']: string}); 

  return (
    <>
      <div className="grid max-h-[70vh] grid-cols-2 gap-2 overflow-auto p-4 pt-0 text-center">
        {registerDataLabels.map((label, index) => (
          <RegisterConfirmCard
            key={`${label}_${index}`}
            title={`${registerData[label]}` || '-'}
            label={registerContactProfileLabels[label]}
          />
        ))}
      </div>
      <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
      <Button className="h-9" text="Previous" onClick={onPrevious} type="secondary" size="large" />
      <Button className="h-9" text="Done" onClick={onDone} type="blue" size="large" />
    </div>
    </>
  );
};

const RegisterForm = ({ onCancel, setRegisterData, initialData }: RegisterFormProps) => {
 // 상태 값에 initialData를 반영
  const [name, setName] = useState<string>(initialData?.name || '');
  const [minimumContactDuration, setMinimumContactDuration] = useState<any>(initialData?.minimumContactDuration || 0);
  const [minimumDegrees, setMinimumDegrees] = useState<any>(initialData?.minimumDegrees || 0);
  const [groundstationData, setGroundstationData] = useState<number | undefined>(initialData?.groundstationId);

  const [groundstationList, setGroundstationList] = useState<any[]>([]);
  const [selectGroundstationList, setSelectGroundstationList] = useState<ListType[]>([]);
  const [nameError, setNameError] = useState<string>('');
  const [durationError, setDurationError] = useState<string>('');
  const [degreesError, setDegreesError] = useState<string>('');
  const [selectedGroundStationInfo, setSelectedGroundStationInfo] = useState<any>(null);

  useEffect(() => {
    fetchGroundstationData();
  }, []);
  
  const fetchGroundstationData = async () => {
    try {
      const res = await fetchGroundStationList();
      const formattedData = res.map((item: any) => ({
        id: item.id,
        name: item.name,
        latitudeDegrees: item.latitudeDegrees,
        longitudeDegrees: item.longitudeDegrees,
        altitudeMeters: item.altitudeMeters,
      }));
      console.log('formattedData',formattedData);
      setGroundstationList(formattedData);

      const selectData = res.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      setSelectGroundstationList(selectData);
      setGroundstationData(formattedData[0].id);
      
      // 첫 번째 지상국 정보를 기본 선택
      if (res.length > 0) {
        setSelectedGroundStationInfo(formattedData[0]);
      }
    } catch (error) {
      console.error('Error fetching groundstation data:', error);
    }
  };

  const validateField = (value: any, condition: any, setError: Dispatch<SetStateAction<string>>, errorMessage: string) => {
    if (condition) {
      setError('');
      return false; // 에러 없음
    } else {
      setError(errorMessage);
      return true; // 에러 있음
    }
  };

  // 지상국 선택 변경 핸들러
  const handleGroundStationChange = (value: number) => {
    setGroundstationData(value);
    const selectedStation = groundstationList.find(station => station.id === value);
    if (selectedStation) {
      setSelectedGroundStationInfo(selectedStation);
    }
  };
  
  const onClickNext = () => {
    let hasErrors = false;

    hasErrors = validateField(name, name.trim(), setNameError, 'Name is required') || hasErrors;
    // 숫자 필드: null 또는 undefined일 경우만 에러 처리 (0은 허용)
    hasErrors = validateField(
      minimumContactDuration, 
      minimumContactDuration !== undefined && minimumContactDuration !== null && !isNaN(minimumContactDuration), 
      setDurationError, 
      'Minimum Contact Duration is required.'
    ) || hasErrors;

    hasErrors = validateField(
      minimumDegrees, 
      minimumDegrees !== undefined && minimumDegrees !== null && !isNaN(minimumDegrees), 
      setDegreesError, 
      'Minimum elevation in degrees is required.'
    ) || hasErrors;

    //에러가 하나라도 있으면 진행 중단
    if (hasErrors) {
      return;
    }

    if (!hasErrors) {
      const selectedGroundstation = selectGroundstationList.find(item => item.value === groundstationData);
      if (selectedGroundstation) {
        setRegisterData({
          groundstationId: selectedGroundstation.value,
          groundstationName: selectedGroundstation.label,
          name,
          minimumContactDuration,
          minimumDegrees,
        });
      }
    }
  };
  
  const handleDownload = async () => {
    try {
      const response = await axios.get('/api/file/download?fileKey=brochure_antenna', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'AntennaBrochure.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('다운로드 실패:', err);
    }
  };

  return (
    <>
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto overflow-x-hidden p-4 pt-0">
        <SelectBox
          className="max-w-none"
          bgColor="layer3"
          label="Ground Station"
          labelTooltip="Ground Station"
          list={selectGroundstationList}
          value={groundstationData}
          setValue={handleGroundStationChange}
        />
        <Label
          label={'Location'}
          htmlFor={'Location'}
          labelTooltip={'Location'}
        />
        <div className='text-sm'>
          - LAT:   {selectedGroundStationInfo?.latitudeDegrees || '-'}  , LNG:  {selectedGroundStationInfo?.longitudeDegrees || '-'}   , ALT:  {selectedGroundStationInfo?.altitudeMeters ? `${selectedGroundStationInfo.altitudeMeters} m` : '-'}
        </div>
        <Input label="Name" labelTooltip="This is the name used when searching for a schedule." value={name} 
          setValue={(value) => { 
            setName(value); 
            validateField(value, value.trim(), setNameError, 'Name is required'); 
          }} 
          errorMessage={nameError} />
        <Input label="Minimum Contact Duration(sec)" labelTooltip="300 seconds or more" type="number" 
          value={minimumContactDuration} 
          setValue={(value) => {
            const numericValue = value === "" ? null : Number(value);
            setMinimumContactDuration(numericValue);
            validateField(numericValue, numericValue !== null, setDurationError, 'Minimum Contact Duration is required.');
          }}
          errorMessage={durationError} />
        <Input label="Minimum elevation in degrees(°)" labelTooltip="Between 0 degrees and 90 degrees" type="number" 
          value={minimumDegrees} 
          setValue={(value) => {
            const numericValue = value === "" ? null : Number(value);
            setMinimumDegrees(numericValue);
            validateField(numericValue, numericValue !== null, setDegreesError, 'Minimum elevation in degrees is required.');
          }}
          errorMessage={degreesError} />
        <div className="flex flex-col gap-2">
          <div className='flex justify-between'>
            <Label
              label={'Antenna Specification'}
              htmlFor={'Antenna Specification'}
              labelTooltip={'Antenna Specification'}
            />
            <Button className="h-9" text="Download" onClick={handleDownload} type="secondary" size="small"/>
          </div>
          <div className="grid gap-y-2">
            <div className='text-sm'>
              - G/T : S Band(≥ 14dB/k), X Band(≥ 27 dB/k)
            </div>
            <div className='text-sm'>
              - Antenna Velocity : AZ: 0.1~20°/s, EL: 0.1~20°/s Tilt: 0.1~5°/s
            </div>
            <div className='text-sm'>
              - Pointing Accuracy : ≤ 0.1°
            </div>
            <div className='text-sm'>
              - Antenna Diameter(m) : 4.5 m
            </div>
            <div className='flex-col text-sm'>
              - Frequency
              <div className="text-sm pl-2">
              Tx : 2025 ~ 2120 MHz
              </div>
              <div className="text-sm pl-2">
              Rx : 2200 ~ 2300 MHz
              </div>
            </div>
          </div>
      </div>
      </div>
      <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
        <Button className="h-9" text="Cancel" onClick={onCancel} type="secondary" size="large" />
        <Button className="h-9" text="Next" onClick={onClickNext} type="blue" size="large" />
      </div>
    </>
  );
};

const RegisterModal = ({ isOpened, setIsOpened, onContactProfileUpdated }: ModalProps) => {
  const [messageArray, setMessageArray] = useState<Message[]>([]);
  const [messageArrayCount, setMessageArrayCount] = useState(0);
  const [registerContactProfileData, setRegisterContactProfileData] = useState<RegisterContactProfile | null>(null);
  const [isConfirmStep, setIsConfirmStep] = useState(false); // 현재 폼/확인 단계 상태

  function onCancel() {
    setIsOpened(false);
    setRegisterContactProfileData(null);
    setIsConfirmStep(false); // 초기화
  }
  const onPrevious = () => {
    setIsConfirmStep(false); // 이전 단계로 돌아가기
  };

  function onClickDeleteButton(index: number) {
    setMessageArray((prev) => prev.filter((message: any) => message.index !== index));
  }

  async function onDone() {
    console.log('registerContactProfileData', registerContactProfileData);
    setIsOpened(false);
    const params = {  
      name : registerContactProfileData?.name,
      minContactDuration : registerContactProfileData?.minimumContactDuration,
      minElevationDegrees : registerContactProfileData?.minimumDegrees,
    };
    const groundStationId = registerContactProfileData?.groundstationId;
    const contactprofileRes = await axios.post(`/api/contactprofile/register/${groundStationId }`, params);
    onContactProfileUpdated();
    setMessageArray((prev) => [
      ...prev,
      {
        index: messageArrayCount,
        icon: <InformationCircleOutlineIcon />,
        text: `Registration has been completed `
      }
    ]);
    setTimeout(() => onClickDeleteButton(messageArrayCount), 2000);
    setMessageArrayCount((prev) => prev + 1);
    setRegisterContactProfileData(null);
  }

  return (
    <>
    <Modal isOpened={isOpened}>
      <ModalInner title="Register a Contact Profile" onClickClose={onCancel}>
        {isConfirmStep && registerContactProfileData ? (
          // RegisterConfirm 단계일 때
          <RegisterConfirm
            onCancel={onCancel}
            onDone={onDone}
            registerData={registerContactProfileData}
            onPrevious={onPrevious} // "Previous" 버튼이 이 함수를 호출
          />
        ) : (
          // RegisterForm 단계일 때 
          <RegisterForm
            onCancel={onCancel}
            setRegisterData={(data) => {
              setRegisterContactProfileData(data);
              setIsConfirmStep(true); // 데이터가 설정되면 다음 단계로 이동
            }}
            initialData={registerContactProfileData}
          />
        )}
      </ModalInner>
    </Modal>
    <InfoMessage messageArray={messageArray} onClickDelete={onClickDeleteButton} />
  </>
  );
};

export default RegisterModal;
